import { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, ChevronLeft, Play, RefreshCw, ListTree, GitBranch, Pencil, Trash2,
  Plus, Zap, Clock, Eye, Navigation, XCircle, CheckCircle2, Circle, AlertCircle, PauseCircle,
} from 'lucide-react'
import {
  getPreparations, updatePreparation, deletePreparation,
  getPreparationSteps, addPreparationStep, removePreparationStep,
  getConnectors, createConnector, deleteConnector,
  addConnectorCondition, removeConnectorCondition, getConnectorConditions,
  getSteps, getJourneysByPreparation, getJourneySteps, triggerJourney,
  getPlates,
} from '../api/brainer'
import { Modal } from '../components/Modal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EmptyState } from '../components/EmptyState'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BPreparation, BPreparationStep, BStep, BConnector, BJourney, BJourneyStep, BPlate } from '../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

type Point = { x: number; y: number }

function ptBottom(el: HTMLElement, ctr: HTMLElement): Point {
  const { left, bottom, width } = el.getBoundingClientRect()
  const cr = ctr.getBoundingClientRect()
  return { x: left + width / 2 - cr.left, y: bottom - cr.top }
}

function ptTop(el: HTMLElement, ctr: HTMLElement): Point {
  const { left, top, width } = el.getBoundingClientRect()
  const cr = ctr.getBoundingClientRect()
  return { x: left + width / 2 - cr.left, y: top - cr.top }
}

function downArrow(from: Point, to: Point): string {
  const cy = Math.abs(to.y - from.y) * 0.5
  return `M ${from.x} ${from.y} C ${from.x} ${from.y + cy} ${to.x} ${to.y - cy} ${to.x} ${to.y - 2}`
}

const TRIGGER_KEY = '__trigger__'

function JourneyStatusBadge({ status }: { status: string }) {
  if (status === 'completed')   return <span className="flex items-center gap-1.5 text-green-600 text-sm"><CheckCircle2 size={15} />Completed</span>
  if (status === 'failed')      return <span className="flex items-center gap-1.5 text-red-500 text-sm"><XCircle size={15} />Failed</span>
  if (status === 'running')     return <span className="flex items-center gap-1.5 text-blue-600 text-sm"><span className="w-3.5 h-3.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin shrink-0" />Running</span>
  if (status === 'paused')      return <span className="flex items-center gap-1.5 text-amber-600 text-sm"><PauseCircle size={15} />Paused</span>
  if (status === 'interrupted') return <span className="flex items-center gap-1.5 text-orange-500 text-sm"><AlertCircle size={15} />Interrupted</span>
  return <span className="flex items-center gap-1.5 text-neutral-400 text-sm"><Circle size={15} />Pending</span>
}

const STEP_STATUS_COLOR: Record<string, string> = {
  pending:   'bg-slate-100 text-neutral-500',
  running:   'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  failed:    'bg-red-50 text-red-700',
  skipped:   'bg-slate-100 text-neutral-400',
}

function journeyDurationSecs(j: BJourney): number {
  if (!j.startedAt || !j.completedAt) return 0
  return (new Date(j.completedAt).getTime() - new Date(j.startedAt).getTime()) / 1000
}

function fmtDuration(j: BJourney): string {
  if (!j.completedAt) return j.status === 'running' ? 'running…' : '—'
  const s = journeyDurationSecs(j)
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

// ── Prep form ─────────────────────────────────────────────────────────────────

type PrepFormData = { name: string; description: string; scheduled: boolean; clock: string; eventSubscription: string; forwardingPlateId: string }

function PrepForm({ form, onChange, plates }: { form: PrepFormData; onChange: (f: PrepFormData) => void; plates: BPlate[] }) {
  const set = (k: keyof PrepFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...form, [k]: k === 'scheduled' ? (e.target as HTMLInputElement).checked : e.target.value })
  return (
    <div className="space-y-4">
      <Field label="Name *">
        <input value={form.name} onChange={set('name')} className="field" />
      </Field>
      <Field label="Description">
        <input value={form.description} onChange={set('description')} className="field" />
      </Field>
      <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
        <input type="checkbox" checked={form.scheduled} onChange={e => onChange({ ...form, scheduled: e.target.checked })} className="rounded" />
        Scheduled (cron)
      </label>
      {form.scheduled && (
        <Field label="Cron expression *">
          <input value={form.clock} onChange={set('clock')} placeholder="0 9 * * *" className="field font-mono" />
        </Field>
      )}
      <Field label="Event subscription">
        <input value={form.eventSubscription} onChange={set('eventSubscription')} placeholder="event.name" className="field" />
      </Field>
      <Field label="Forwarding plate" description="After a journey completes, redirect to this plate">
        <select value={form.forwardingPlateId} onChange={e => onChange({ ...form, forwardingPlateId: e.target.value })} className="field">
          <option value="">— none (go to journey detail) —</option>
          {plates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
    </div>
  )
}

// ── Workflow graph ─────────────────────────────────────────────────────────────

type Edge = { d: string; color: string; marker: string; from: string; to: string }

const STEP_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  straw:       { border: 'border-blue-300',   bg: 'bg-blue-50',   text: 'text-blue-700'   },
  drinker:     { border: 'border-amber-300',  bg: 'bg-amber-50',  text: 'text-amber-700'  },
  interviewer: { border: 'border-green-300',  bg: 'bg-green-50',  text: 'text-green-700'  },
  connector:   { border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700' },
}

function WorkflowGraph({
  prep,
  prepSteps,
  connectors,
}: {
  prep: BPreparation
  prepSteps: BPreparationStep[]
  connectors: BConnector[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef   = useRef<HTMLDivElement>(null)
  const stepRefs     = useRef<Map<string, HTMLDivElement>>(new Map())

  const [edges,       setEdges]       = useState<Edge[]>([])
  const [svgSize,     setSvgSize]     = useState({ w: 0, h: 0 })
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const sorted    = useMemo(() => [...prepSteps].sort((a, b) => a.position - b.position), [prepSteps])
  const firstStep = sorted[0]

  useLayoutEffect(() => {
    function recompute() {
      const ctr = containerRef.current
      if (!ctr) return
      setSvgSize({ w: ctr.offsetWidth, h: ctr.scrollHeight })

      const next: Edge[] = []

      if (triggerRef.current && firstStep) {
        const firstEl = stepRefs.current.get(firstStep.id)
        if (firstEl) {
          next.push({
            d: downArrow(ptBottom(triggerRef.current, ctr), ptTop(firstEl, ctr)),
            color: '#98a2b3', marker: 'ah-slate', from: TRIGGER_KEY, to: firstStep.id,
          })
        }
      }

      for (const conn of connectors) {
        const fromEl = stepRefs.current.get(conn.fromStepId)
        const toEl   = stepRefs.current.get(conn.toStepId)
        if (!fromEl || !toEl) continue
        next.push({
          d: downArrow(ptBottom(fromEl, ctr), ptTop(toEl, ctr)),
          color: '#6366f1', marker: 'ah-indigo', from: conn.fromStepId, to: conn.toStepId,
        })
      }

      setEdges(next)
    }

    recompute()
    const ro = new ResizeObserver(recompute)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [sorted, connectors, firstStep])

  const toggle = (k: string) => setSelectedKey(p => p === k ? null : k)

  const highlighted = useMemo<Set<string> | null>(() => {
    if (!selectedKey) return null
    const s = new Set<string>([selectedKey])
    for (const e of edges) {
      if (e.from === selectedKey || e.to === selectedKey) { s.add(e.from); s.add(e.to) }
    }
    return s
  }, [selectedKey, edges])

  const nodeRing = (k: string) => {
    if (!highlighted) return ''
    if (k === selectedKey) return 'ring-2 ring-blue-400 ring-offset-1'
    if (highlighted.has(k)) return 'ring-1 ring-blue-200 ring-offset-1'
    return 'opacity-30'
  }

  const edgeOpacity = (e: Edge) => {
    if (!highlighted) return 0.7
    return highlighted.has(e.from) && highlighted.has(e.to) ? 1 : 0.08
  }

  const trigType   = prep.scheduled ? 'scheduled' : prep.eventSubscription ? 'event' : 'manual'
  const trigBorder = trigType === 'scheduled' ? 'border-blue-300' : trigType === 'event' ? 'border-purple-300' : 'border-slate-300'
  const nodeBase   = 'bg-white border rounded-lg px-4 py-3 shadow-sm relative z-10 cursor-pointer select-none transition-opacity'

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col items-center gap-10 py-10 min-h-[300px] w-full"
      onClick={e => { if (e.target === e.currentTarget) setSelectedKey(null) }}
    >
      <svg
        className="absolute top-0 left-0 pointer-events-none z-0"
        width={svgSize.w}
        height={svgSize.h}
        style={{ overflow: 'visible' }}
      >
        <defs>
          {([{ id: 'ah-slate', fill: '#98a2b3' }, { id: 'ah-indigo', fill: '#6366f1' }] as const).map(({ id, fill }) => (
            <marker key={id} id={id} markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <path d="M0,0 L8,3 L0,6 Z" fill={fill} />
            </marker>
          ))}
        </defs>
        {edges.map((e, i) => (
          <path
            key={i} d={e.d} fill="none"
            stroke={e.color} strokeWidth={1.5}
            opacity={edgeOpacity(e)}
            markerEnd={`url(#${e.marker})`}
          />
        ))}
      </svg>

      {/* Trigger */}
      <div
        ref={triggerRef}
        className={`${nodeBase} ${trigBorder} ${nodeRing(TRIGGER_KEY)}`}
        style={{ minWidth: 200 }}
        onClick={() => toggle(TRIGGER_KEY)}
      >
        <div className="flex items-center gap-2 mb-0.5">
          {trigType === 'scheduled'
            ? <Clock  size={13} className="text-blue-400   shrink-0" />
            : trigType === 'event'
              ? <Zap  size={13} className="text-purple-400 shrink-0" />
              : <Play size={13} className="text-slate-400  shrink-0" />}
          <span className="text-sm font-semibold text-neutral-800 capitalize">{trigType}</span>
        </div>
        {prep.clock             && <p className="font-mono text-[10px] text-blue-400   truncate">{prep.clock}</p>}
        {prep.eventSubscription && <p className="font-mono text-[10px] text-purple-400 truncate">{prep.eventSubscription}</p>}
        {trigType === 'manual'  && <p className="text-[10px] text-neutral-300">on demand</p>}
      </div>

      {/* Steps */}
      {sorted.length === 0 ? (
        <div className="text-sm text-neutral-400 border border-dashed border-slate-200 rounded-lg px-10 py-8 text-center">
          No steps — add some from the Steps button above
        </div>
      ) : sorted.map(ps => {
        const t  = ps.step?.type ?? 'straw'
        const ts = STEP_COLORS[t] ?? STEP_COLORS.straw
        return (
          <div
            key={ps.id}
            ref={el => { if (el) stepRefs.current.set(ps.id, el); else stepRefs.current.delete(ps.id) }}
            className={`${nodeBase} ${ts.border} ${nodeRing(ps.id)}`}
            style={{ minWidth: 200 }}
            onClick={() => toggle(ps.id)}
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-neutral-300 w-5 text-center shrink-0">{ps.position}</span>
              <span className="text-sm font-semibold text-neutral-800">
                {ps.step?.title ?? ps.stepId.slice(0, 8)}
              </span>
              {ps.step?.type && (
                <span className={`text-[9px] px-1.5 py-px rounded border font-medium shrink-0 ${ts.bg} ${ts.text}`}>
                  {ps.step.type}
                </span>
              )}
              {ps.mandatory && (
                <span className="text-[9px] px-1 py-px rounded bg-slate-100 text-neutral-400 shrink-0">mand.</span>
              )}
            </div>
            {ps.step?.description && (
              <p className="text-[10px] text-neutral-400 mt-0.5 pl-7 max-w-xs">{ps.step.description}</p>
            )}
          </div>
        )
      })}

      {/* Legend */}
      <div className="absolute bottom-3 right-3 flex items-center gap-4">
        {[{ color: '#98a2b3', marker: 'leg-slate', label: 'Entry' }, { color: '#6366f1', marker: 'leg-indigo', label: 'Connector' }].map(({ color, marker, label }) => (
          <span key={label} className="flex items-center gap-1.5 text-[10px] text-neutral-400">
            <svg width={22} height={12} style={{ overflow: 'visible' }}>
              <defs>
                <marker id={marker} markerWidth="6" markerHeight="5" refX="6" refY="2.5" orient="auto">
                  <path d="M0,0 L6,2.5 L0,5 Z" fill={color} />
                </marker>
              </defs>
              <line x1={0} y1={6} x2={16} y2={6} stroke={color} strokeWidth={1.5} markerEnd={`url(#${marker})`} />
            </svg>
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Runs chart ────────────────────────────────────────────────────────────────

const CHART_H = 140
const LABEL_H = 24
const YAXIS_W = 36
const BAR_MIN_H = 3

function RunsChart({ journeys }: { journeys: BJourney[] }) {
  const fmt = useDateFmt()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth]   = useState(0)
  const [hovered, setHovered] = useState<number | null>(null)

  useLayoutEffect(() => {
    const measure = () => { if (wrapRef.current) setWidth(wrapRef.current.offsetWidth) }
    measure()
    const ro = new ResizeObserver(measure)
    if (wrapRef.current) ro.observe(wrapRef.current)
    return () => ro.disconnect()
  }, [])

  const recent = useMemo(() => [...journeys].reverse().slice(-40), [journeys])
  const durations = useMemo(() => recent.map(journeyDurationSecs), [recent])
  const maxDur = Math.max(...durations, 1)

  const n     = recent.length
  const innerW = width - YAXIS_W
  const barW  = n === 0 ? 0 : Math.max(6, Math.floor((innerW - (n - 1) * 3) / n))
  const gap   = n <= 1 ? 0 : (innerW - n * barW) / Math.max(n - 1, 1)

  const barColor = (j: BJourney) => {
    if (j.status === 'failed')  return '#f04438'
    if (j.status === 'running') return '#465fff'
    if (j.status === 'paused')  return '#f79009'
    return '#12b76a'
  }

  const yTicks = [0, Math.round(maxDur / 2), Math.round(maxDur)]

  if (width === 0) return <div ref={wrapRef} style={{ height: CHART_H + LABEL_H }} />

  return (
    <div ref={wrapRef} className="select-none">
      <svg width={width} height={CHART_H + LABEL_H} className="overflow-visible">
        {yTicks.map((v, i) => {
          const y = CHART_H - Math.max(0, (v / maxDur) * (CHART_H - BAR_MIN_H))
          return (
            <g key={i}>
              <line x1={YAXIS_W} y1={y} x2={width} y2={y} stroke="#f2f4f7" strokeWidth={1} />
              {v > 0 && (
                <text x={YAXIS_W - 5} y={y + 4} textAnchor="end" fontSize={9} fill="#98a2b3">
                  {v >= 60 ? `${Math.floor(v / 60)}m` : `${v}s`}
                </text>
              )}
            </g>
          )
        })}

        {recent.map((j, i) => {
          const dur  = durations[i]
          const barH = Math.max(BAR_MIN_H, (dur / maxDur) * (CHART_H - BAR_MIN_H))
          const x    = YAXIS_W + i * (barW + gap)
          const y    = CHART_H - barH
          const isHov = hovered === i
          const ttW = 120; const ttH = 38
          const ttX = Math.min(Math.max(x - ttW / 2 + barW / 2, YAXIS_W), width - ttW)
          const ttY = Math.max(0, y - ttH - 6)
          return (
            <g key={j.id} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'default' }}>
              <rect x={x} y={y} width={barW} height={barH} rx={2} fill={barColor(j)} opacity={isHov ? 1 : 0.72} />
              {isHov && (
                <g>
                  <rect x={ttX} y={ttY} width={ttW} height={ttH} rx={4} fill="#1d2939" />
                  <text x={ttX + 8} y={ttY + 14} fontSize={10} fill="white" fontWeight="600">{fmtDuration(j)}</text>
                  <text x={ttX + 8} y={ttY + 27} fontSize={9} fill="#98a2b3">{j.startedAt ? fmt(j.startedAt) : '—'}</text>
                </g>
              )}
            </g>
          )
        })}

        {recent.map((j, i) => {
          const show = n <= 12 || i % Math.ceil(n / 10) === 0 || i === n - 1
          if (!show) return null
          return (
            <text key={j.id} x={YAXIS_W + i * (barW + gap) + barW / 2} y={CHART_H + LABEL_H - 4}
              textAnchor="middle" fontSize={9} fill="#98a2b3">
              {j.startedAt ? new Date(j.startedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
            </text>
          )
        })}
      </svg>

      <div className="flex items-center gap-4 mt-1">
        {[{ color: '#12b76a', label: 'Completed' }, { color: '#f04438', label: 'Failed' }, { color: '#465fff', label: 'Running' }, { color: '#f79009', label: 'Paused' }].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1 text-[10px] text-neutral-400">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Journey steps modal ───────────────────────────────────────────────────────

function JourneyStepsModal({
  journey,
  prepSteps,
  onClose,
}: {
  journey: BJourney
  prepSteps: BPreparationStep[]
  onClose: () => void
}) {
  const fmt = useDateFmt()
  const qc  = useQueryClient()

  const { data: steps = [], isLoading } = useQuery({
    queryKey: ['b-journey-steps', journey.id],
    queryFn:  () => getJourneySteps(journey.id).then(r => r.data),
    refetchInterval: journey.status === 'running' ? 1500 : false,
  })

  const stepTitle = (js: BJourneyStep) =>
    prepSteps.find(ps => ps.id === js.preparationStepId)?.step?.title ?? js.preparationStepId.slice(0, 8)

  return (
    <Modal title="Journey steps" onClose={onClose} size="lg">
      <div className="space-y-3">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <JourneyStatusBadge status={journey.status} />
          <span className="text-xs text-neutral-400">{journey.startedAt ? `Started: ${fmt(journey.startedAt)}` : 'Not started'}</span>
          {journey.completedAt && <span className="text-xs text-neutral-400">Completed: {fmt(journey.completedAt)}</span>}
          <span className="text-xs text-neutral-400 ml-auto">{fmtDuration(journey)}</span>
          {journey.status === 'running' && (
            <button onClick={() => qc.invalidateQueries({ queryKey: ['b-journey-steps', journey.id] })}
              className="flex items-center gap-1 text-xs text-blue-600">
              <RefreshCw size={11} /> Refresh
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-neutral-400">Loading…</div>
        ) : (steps as BJourneyStep[]).length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">No steps executed yet</p>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {(steps as BJourneyStep[]).map((s, idx) => (
              <div key={s.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs font-mono text-neutral-400 w-5 shrink-0">{idx + 1}</span>
                  <span className="text-xs font-medium text-neutral-700 flex-1">{stepTitle(s)}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STEP_STATUS_COLOR[s.status]}`}>
                    {s.status === 'running' && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse inline-block" />}
                    {s.status}
                  </span>
                  {s.completedAt && s.startedAt && (
                    <span className="text-xs text-neutral-400 font-mono">
                      {((new Date(s.completedAt).getTime() - new Date(s.startedAt).getTime()) / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>
                {s.payload && Object.keys(s.payload).length > 0 && (
                  <div className="mt-2 ml-8">
                    <p className="text-xs font-medium text-neutral-500 mb-1">Payload</p>
                    <pre className="text-xs bg-slate-50 rounded p-2 overflow-x-auto text-neutral-700 max-h-28">{JSON.stringify(s.payload, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ── Runs tab ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

function RunsTab({ prep, prepSteps }: { prep: BPreparation; prepSteps: BPreparationStep[] }) {
  const fmt = useDateFmt()
  const qc  = useQueryClient()

  const [page, setPage]             = useState(1)
  const [viewJourney, setViewJourney] = useState<BJourney | null>(null)
  const [showTrigger, setShowTrigger] = useState(false)

  const { data, refetch } = useQuery({
    queryKey: ['b-journeys-prep', prep.id, page],
    queryFn:  () => getJourneysByPreparation(prep.id, page, PAGE_SIZE).then(r => r.data),
    refetchInterval: (q: any) =>
      (q?.state?.data?.runningCount ?? 0) > 0 ? 2000 : false,
  })

  const journeys:      BJourney[] = data?.data          ?? []
  const total:         number     = data?.total          ?? 0
  const totalPages:    number     = data?.totalPages      ?? 1
  const completedCount             = data?.completedCount ?? 0
  const failedCount                = data?.failedCount    ?? 0
  const runningCount               = data?.runningCount   ?? 0

  const trigger = useMutation({
    mutationFn: () => triggerJourney(prep.id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['b-journeys-prep', prep.id] })
      setShowTrigger(false)
      setPage(1)
    },
  })

  return (
    <div>
      {/* Stats strip */}
      <div className="flex items-center gap-6 mb-5">
        <div className="text-center">
          <p className="text-lg font-semibold text-neutral-900">{total}</p>
          <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Total</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-green-600">{completedCount}</p>
          <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Completed</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-red-500">{failedCount}</p>
          <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Failed</p>
        </div>
        {runningCount > 0 && (
          <div className="text-center">
            <p className="text-lg font-semibold text-blue-600">{runningCount}</p>
            <p className="text-[10px] text-neutral-400 uppercase tracking-wide">Running</p>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => refetch()} icon={RefreshCw}>Refresh</Button>
          <Button onClick={() => setShowTrigger(true)} icon={Play}>Trigger</Button>
        </div>
      </div>

      {/* Chart */}
      <div className="border border-slate-100 rounded-lg p-4 mb-5">
        <p className="text-theme-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
          Run duration — page {page}
        </p>
        {journeys.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-neutral-400 border border-dashed border-slate-200 rounded-lg">
            No runs yet — click Trigger to start
          </div>
        ) : (
          <RunsChart journeys={[...journeys].reverse()} />
        )}
      </div>

      {/* Table */}
      {total === 0 ? (
        <EmptyState icon={Navigation} title="No runs" description="Trigger this preparation to see journey history" />
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Triggered by</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {journeys.map(j => (
                  <tr key={j.id}>
                    <td><JourneyStatusBadge status={j.status} /></td>
                    <td>
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-neutral-600">{j.triggeredBy}</span>
                    </td>
                    <td className="text-neutral-400 text-xs">{j.startedAt ? fmt(j.startedAt) : '—'}</td>
                    <td className="text-neutral-400 text-xs font-mono">{fmtDuration(j)}</td>
                    <td className="text-right">
                      <button onClick={() => setViewJourney(j)} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 ml-auto">
                        <Eye size={13} /> Steps
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-1">
            <span className="text-theme-xs text-neutral-400">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
                className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-theme-xs text-neutral-600 px-2">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages}
                className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Trigger confirm */}
      {showTrigger && (
        <Modal title="Trigger journey" onClose={() => setShowTrigger(false)} size="sm">
          <p className="text-sm text-neutral-600 mb-4">
            Manually trigger a new journey run for <strong>{prep.name}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowTrigger(false)}>Cancel</Button>
            <Button onClick={() => trigger.mutate()} disabled={trigger.isPending} icon={Play}>
              {trigger.isPending ? 'Triggering…' : 'Trigger'}
            </Button>
          </div>
        </Modal>
      )}

      {viewJourney && (
        <JourneyStepsModal journey={viewJourney} prepSteps={prepSteps} onClose={() => setViewJourney(null)} />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'workflow' | 'runs'

export function BrainerPreparationDetail() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const fmt      = useDateFmt()

  const [tab, setTab] = useState<Tab>('workflow')

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: preps = [] } = useQuery({
    queryKey: ['b-preps'],
    queryFn:  () => getPreparations().then(r => r.data),
  })
  const prep: BPreparation | undefined = (preps as BPreparation[]).find(p => p.id === id)

  const { data: prepSteps = [], isLoading: stepsLoading } = useQuery({
    queryKey: ['b-prep-steps', id],
    queryFn:  () => id ? getPreparationSteps(id).then(r => r.data) : [],
    enabled:  !!id,
  })

  const { data: connectors = [] } = useQuery({
    queryKey: ['b-connectors', id],
    queryFn:  () => id ? getConnectors(id).then(r => r.data) : [],
    enabled:  !!id,
  })

  const { data: allSteps = [] } = useQuery({
    queryKey: ['b-steps'],
    queryFn:  () => getSteps().then(r => r.data),
  })

  const { data: plates = [] } = useQuery({
    queryKey: ['b-plates'],
    queryFn:  () => getPlates().then(r => r.data),
  })

  // ── Edit ──────────────────────────────────────────────────────────────────

  const blankForm = { name: '', description: '', scheduled: false, clock: '', eventSubscription: '', forwardingPlateId: '' }
  const [showEdit, setShowEdit] = useState(false)
  const [editForm, setEditForm] = useState(blankForm)

  const openEdit = () => {
    if (!prep) return
    setEditForm({ name: prep.name, description: prep.description ?? '', scheduled: prep.scheduled, clock: prep.clock ?? '', eventSubscription: prep.eventSubscription ?? '', forwardingPlateId: prep.forwardingPlateId ?? '' })
    setShowEdit(true)
  }

  const saveMut = useMutation({
    mutationFn: () => updatePreparation(id!, { name: editForm.name, description: editForm.description || undefined, scheduled: editForm.scheduled, clock: editForm.clock || undefined, eventSubscription: editForm.eventSubscription || undefined, forwardingPlateId: editForm.forwardingPlateId || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-preps'] }); setShowEdit(false) },
  })

  // ── Delete ────────────────────────────────────────────────────────────────

  const [showDelete, setShowDelete] = useState(false)
  const delMut = useMutation({
    mutationFn: () => deletePreparation(id!),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['b-preps'] }); navigate('/brainer/preparations') },
  })

  // ── Steps modal ───────────────────────────────────────────────────────────

  const [showSteps, setShowSteps] = useState(false)
  const [addStepForm, setAddStepForm] = useState({ stepId: '', mandatory: true, position: '0' })

  const addedStepIds = new Set((prepSteps as BPreparationStep[]).map(ps => ps.stepId))
  const availableSteps = (allSteps as BStep[]).filter(s => !addedStepIds.has(s.id))

  const addStepMut = useMutation({
    mutationFn: () => addPreparationStep(id!, { stepId: addStepForm.stepId, mandatory: addStepForm.mandatory, position: +addStepForm.position }),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['b-prep-steps', id] }); setAddStepForm({ stepId: '', mandatory: true, position: String((prepSteps as BPreparationStep[]).length) }) },
  })

  const removeStepMut = useMutation({
    mutationFn: (psId: string) => removePreparationStep(psId),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['b-prep-steps', id] }),
  })

  // ── Connectors modal ──────────────────────────────────────────────────────

  const [showConns, setShowConns] = useState(false)
  const [connForm,  setConnForm]  = useState({ fromStepId: '', toStepId: '' })

  const addConnMut = useMutation({
    mutationFn: () => createConnector({ preparationId: id!, fromStepId: connForm.fromStepId, toStepId: connForm.toStepId }),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['b-connectors', id] }); setConnForm({ fromStepId: '', toStepId: '' }) },
  })

  const delConnMut = useMutation({
    mutationFn: (cid: string) => deleteConnector(cid),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['b-connectors', id] }),
  })

  const [condConnId, setCondConnId] = useState<string | null>(null)
  const { data: conditions = [] } = useQuery({
    queryKey: ['b-conditions', condConnId],
    queryFn:  () => condConnId ? getConnectorConditions(condConnId).then(r => r.data) : [],
    enabled:  !!condConnId,
  })
  const [condScript, setCondScript] = useState('')
  const addCondMut = useMutation({
    mutationFn: () => addConnectorCondition(condConnId!, { script: condScript }),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['b-conditions', condConnId] }); setCondScript('') },
  })
  const delCondMut = useMutation({
    mutationFn: (cid: string) => removeConnectorCondition(cid),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['b-conditions', condConnId] }),
  })

  const psLabel = (psId: string) => {
    const ps = (prepSteps as BPreparationStep[]).find(p => p.id === psId)
    return ps?.step?.title ?? psId.slice(0, 8)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!prep && preps.length > 0) return (
    <div className="py-16 text-center text-sm text-red-500">Preparation not found</div>
  )
  if (!prep) return (
    <div className="py-16 text-center text-sm text-neutral-400">Loading…</div>
  )

  const sortedPrepSteps = [...(prepSteps as BPreparationStep[])].sort((a, b) => a.position - b.position)
  const trigBadge = prep.scheduled
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"><Clock size={10} /> {prep.clock}</span>
    : prep.eventSubscription
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700"><Zap size={10} /> {prep.eventSubscription}</span>
      : <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-neutral-600">manual</span>

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-3 text-sm">
        <button onClick={() => navigate('/brainer/preparations')}
          className="text-neutral-400 hover:text-neutral-700 transition-colors">
          Preparations
        </button>
        <ChevronRight size={13} className="text-neutral-300" />
        <span className="text-neutral-700 font-medium">{prep.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-semibold text-neutral-900">{prep.name}</h1>
            {trigBadge}
          </div>
          {prep.description && <p className="text-sm text-neutral-400 mt-0.5">{prep.description}</p>}
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <Button variant="secondary" onClick={() => setShowSteps(true)} icon={ListTree}>
            Steps
          </Button>
          <Button variant="secondary" onClick={() => setShowConns(true)} icon={GitBranch}>
            Connectors
          </Button>
          <Button variant="secondary" onClick={openEdit} icon={Pencil}>
            Edit
          </Button>
          <Button variant="danger" onClick={() => setShowDelete(true)} icon={Trash2}>
            Delete
          </Button>
        </div>
      </div>

      <p className="text-xs text-neutral-300 mb-5">
        Created {fmt(prep.createdAt)}
      </p>

      {/* Tabs */}
      <div className="border-b border-slate-100 mb-5">
        <nav className="flex -mb-px">
          {(['workflow', 'runs'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-gray-500 hover:text-neutral-700'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tab === 'workflow' ? (
        stepsLoading ? (
          <div className="py-16 text-center text-sm text-neutral-400">Loading…</div>
        ) : (
          <WorkflowGraph
            prep={prep}
            prepSteps={prepSteps as BPreparationStep[]}
            connectors={connectors as BConnector[]}
          />
        )
      ) : (
        <RunsTab
          prep={prep}
          prepSteps={prepSteps as BPreparationStep[]}
        />
      )}

      {/* Edit modal */}
      {showEdit && (
        <Modal title={`Edit — ${prep.name}`} onClose={() => setShowEdit(false)}>
          <PrepForm form={editForm} onChange={setEditForm} plates={plates as BPlate[]} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!editForm.name || saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {showDelete && (
        <ConfirmDialog
          title="Delete preparation"
          message={`Delete "${prep.name}" and all its journey history?`}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => delMut.mutate()}
          loading={delMut.isPending}
        />
      )}

      {/* Steps modal */}
      {showSteps && (
        <Modal title={`Steps — ${prep.name}`} onClose={() => setShowSteps(false)} size="lg">
          <div className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Field label="Step">
                  <select value={addStepForm.stepId} onChange={e => setAddStepForm(f => ({ ...f, stepId: e.target.value }))} className="field">
                    <option value="">Select step</option>
                    {availableSteps.map(s => <option key={s.id} value={s.id}>{s.title} ({s.type})</option>)}
                  </select>
                </Field>
              </div>
              <div>
                <Field label="Position">
                  <input type="number" value={addStepForm.position} min="0"
                    onChange={e => setAddStepForm(f => ({ ...f, position: e.target.value }))} className="w-20 field" />
                </Field>
              </div>
              <div className="flex items-center gap-3 pb-0.5">
                <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
                  <input type="checkbox" checked={addStepForm.mandatory} onChange={e => setAddStepForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded" />
                  Mandatory
                </label>
                <Button onClick={() => addStepMut.mutate()} disabled={!addStepForm.stepId || addStepMut.isPending} icon={Plus} />
              </div>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {sortedPrepSteps.map(ps => (
                <div key={ps.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-neutral-400 w-6 text-center">{ps.position}</span>
                    <div>
                      <p className="text-sm font-medium text-neutral-800">{ps.step?.title ?? ps.stepId.slice(0, 8)}</p>
                      {ps.step?.type && (
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                          ps.step.type === 'straw' ? 'bg-blue-50 text-blue-700' : ps.step.type === 'drinker' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                        }`}>{ps.step.type}</span>
                      )}
                    </div>
                    {ps.mandatory && <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-slate-100 text-neutral-500">mandatory</span>}
                  </div>
                  <button onClick={() => removeStepMut.mutate(ps.id)} className="text-neutral-300 hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              {sortedPrepSteps.length === 0 && <p className="text-sm text-neutral-400 text-center py-4">No steps added yet</p>}
            </div>
          </div>
        </Modal>
      )}

      {/* Connectors modal */}
      {showConns && (
        <Modal title={`Connectors — ${prep.name}`} onClose={() => setShowConns(false)} size="lg">
          <div className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Field label="From step">
                  <select value={connForm.fromStepId} onChange={e => setConnForm(f => ({ ...f, fromStepId: e.target.value }))} className="field">
                    <option value="">Select</option>
                    {sortedPrepSteps.map(ps => <option key={ps.id} value={ps.id}>{ps.step?.title ?? ps.id.slice(0, 8)}</option>)}
                  </select>
                </Field>
              </div>
              <div className="flex-1">
                <Field label="To step">
                  <select value={connForm.toStepId} onChange={e => setConnForm(f => ({ ...f, toStepId: e.target.value }))} className="field">
                    <option value="">Select</option>
                    {sortedPrepSteps.map(ps => <option key={ps.id} value={ps.id}>{ps.step?.title ?? ps.id.slice(0, 8)}</option>)}
                  </select>
                </Field>
              </div>
              <Button onClick={() => addConnMut.mutate()} disabled={!connForm.fromStepId || !connForm.toStepId || addConnMut.isPending} className="mb-0.5" icon={Plus} />
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(connectors as BConnector[]).length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-4">No connectors defined</p>
              ) : (connectors as BConnector[]).map(conn => (
                <div key={conn.id} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-neutral-800">{psLabel(conn.fromStepId)}</span>
                      <span className="text-neutral-400">→</span>
                      <span className="font-medium text-neutral-800">{psLabel(conn.toStepId)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setCondConnId(condConnId === conn.id ? null : conn.id)} className="text-xs text-neutral-500 hover:text-neutral-800">conditions</button>
                      <button onClick={() => delConnMut.mutate(conn.id)} className="text-neutral-300 hover:text-red-500"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {condConnId === conn.id && (
                    <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1">
                      {(conditions as any[]).map(c => (
                        <div key={c.id} className="flex items-center gap-2 text-xs">
                          <code className="flex-1 bg-slate-50 px-2 py-1 rounded font-mono truncate">{c.script}</code>
                          <button onClick={() => delCondMut.mutate(c.id)} className="text-neutral-300 hover:text-red-500"><Trash2 size={11} /></button>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-1">
                        <input value={condScript} onChange={e => setCondScript(e.target.value)} placeholder="return score > 50;"
                          className="flex-1 px-2 py-1.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400" />
                        <button onClick={() => addCondMut.mutate()} disabled={!condScript || addCondMut.isPending}
                          className="px-2 py-1.5 text-xs rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60">
                          <Plus size={11} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
